import React, { useState } from 'react';
import FadeIn from '../components/FadeIn';
import SEO from '../components/SEO';
import { useEffect } from 'react';

const SALARY_DATA = {
    'customer-service': 45380,
    'back-office': 42070,
    'it-support': 61550,
    'finance': 81680,
    'hr': 72910,
    'sales': 66780
};

const Calculator = () => {
    const [calculatorInputs, setCalculatorInputs] = useState({
        employees: 5,
        avgSalary: 60000,
        processes: 'customer-service'
    });
    const [calculatorResults, setCalculatorResults] = useState(null);

    // Auto-fill salary based on process type
    useEffect(() => {
        if (SALARY_DATA[calculatorInputs.processes]) {
            setCalculatorInputs(prev => ({
                ...prev,
                avgSalary: SALARY_DATA[calculatorInputs.processes]
            }));
        }
    }, [calculatorInputs.processes]);

    const calculateSavings = () => {
        const { employees, avgSalary, processes } = calculatorInputs;

        const processMultipliers = {
            'customer-service': 0.65,
            'back-office': 0.70,
            'it-support': 0.60,
            'finance': 0.62,
            'hr': 0.68,
            'sales': 0.63
        };

        const phCostRatio = processMultipliers[processes] || 0.65;
        const currentAnnualCost = employees * avgSalary;
        const phAnnualCost = currentAnnualCost * phCostRatio;
        const annualSavings = currentAnnualCost - phAnnualCost;
        const savingsPercentage = ((annualSavings / currentAnnualCost) * 100).toFixed(1);
        const threeYearSavings = annualSavings * 3;
        const breakEvenMonths = 2;

        setCalculatorResults({
            currentAnnualCost,
            phAnnualCost,
            annualSavings,
            savingsPercentage,
            threeYearSavings,
            breakEvenMonths
        });
    };

    return (
        <div className="pt-20 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
            <SEO
                pageKey="calculator"
                defaultTitle="Offshore Savings Calculator | Calculate BPO ROI"
                defaultDescription="Calculate your potential savings with AI-augmented offshore staffing. Compare costs for Customer Service, IT, Back Office, and more."
            />
            <div className="max-w-4xl mx-auto">
                <FadeIn>
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-[#20222D] mb-4">Offshore Savings Calculator</h1>
                        <p className="text-lg text-gray-600">
                            Estimate how much you can save by building an AI-augmented team in the Philippines.
                        </p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                        <div className="p-8 md:p-12">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Number of Employees</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={calculatorInputs.employees}
                                            onChange={(e) => setCalculatorInputs({ ...calculatorInputs, employees: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#A2CB52] focus:border-transparent outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Average Annual Salary (USD)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={calculatorInputs.avgSalary}
                                            onChange={(e) => setCalculatorInputs({ ...calculatorInputs, avgSalary: parseInt(e.target.value) || 0 })}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#A2CB52] focus:border-transparent outline-none"
                                        />
                                        <div className="mt-2 space-y-1">
                                            <p className="text-[10px] text-gray-400">
                                                Data sourced from U.S. Bureau of Labor Statistics (BLS).
                                            </p>
                                            <p className="text-[10px] text-gray-400 font-medium">
                                                Last updated: May 2024
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Process Type</label>
                                        <select
                                            value={calculatorInputs.processes}
                                            onChange={(e) => setCalculatorInputs({ ...calculatorInputs, processes: e.target.value })}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#A2CB52] focus:border-transparent outline-none"
                                        >
                                            <option value="customer-service">Customer Service</option>
                                            <option value="back-office">Back Office / Data Entry</option>
                                            <option value="it-support">IT Support / Dev</option>
                                            <option value="finance">Finance & Accounting</option>
                                            <option value="hr">Human Resources</option>
                                            <option value="sales">Sales & Lead Gen</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={calculateSavings}
                                        className="w-full bg-[#20222D] text-white font-bold py-4 rounded-xl hover:bg-[#2a2d3d] transition-colors shadow-lg"
                                    >
                                        Calculate Savings
                                    </button>
                                    <div className="mt-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100/50">
                                        <p className="text-[10px] leading-relaxed text-blue-700">
                                            <strong className="block mb-1">Disclaimer & Context:</strong>
                                            Estimated savings percentages are projections based on industry averages and may vary depending on years of experience, specialization, and specific company requirements. Senior-level roles or highly specialized technical positions may increase the projected cost difference.
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-6 flex flex-col justify-center">
                                    {calculatorResults ? (
                                        <div className="space-y-6">
                                            <div className="text-center">
                                                <p className="text-sm text-gray-500 uppercase tracking-wide font-bold">Estimated Annual Savings</p>
                                                <p className="text-4xl lg:text-5xl font-bold text-[#A2CB52] mt-2">
                                                    ${calculatorResults.annualSavings.toLocaleString()}
                                                </p>
                                                <p className="text-sm text-green-600 font-bold mt-1">
                                                    {calculatorResults.savingsPercentage}% Reduction
                                                </p>
                                            </div>

                                            <div className="space-y-3 pt-4 border-t border-gray-200">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Current Cost:</span>
                                                    <span className="font-bold text-gray-900">${calculatorResults.currentAnnualCost.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Offshore Cost:</span>
                                                    <span className="font-bold text-[#20222D]">${calculatorResults.phAnnualCost.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between text-sm pt-2">
                                                    <span className="text-gray-600">3-Year Savings:</span>
                                                    <span className="font-bold text-[#A2CB52]">${calculatorResults.threeYearSavings.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-gray-400">
                                            <p className="mb-2">📊</p>
                                            <p>Enter your details and click calculate to see your potential savings.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default Calculator;
